"use client";

import { memo } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, XCircle, Copy, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PaymentStatus } from "../donationModalReducer";
import type { BankWithAccounts } from "@/types";
import type {
  MoyasarCardFieldErrors,
  MoyasarTokenizeCardParams,
} from "@/lib/utils/moyasar";
import type { UseFormReturn } from "react-hook-form";
import type { Value } from "react-phone-number-input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { CustomCardForm } from "../CustomCardForm";
import { ApplePayCheckout } from "../ApplePayCheckout";
import type { UnifiedDonationProjectParams } from "../unifiedDonationTypes";
import { CreditCardAcceptIcon } from "@/components/icons/CreditCardAcceptIcon";
import { BankIcon } from "@/components/icons/BankIcon";

export interface DonationPaymentStepProps {
  total: number;
  paymentStatus: PaymentStatus;
  paymentError: string | null;
  paymentMethodTab: "card" | "bankTransfer";
  setPaymentMethodTab: (tab: "card" | "bankTransfer") => void;
  moyasarConfig: unknown;
  onRetry: () => void;
  onBack: () => void;
  locale: string;
  t: (key: string) => string;
  onApplePaySuccess?: (operationCode: string) => void;
  onApplePayModalError?: (message: string) => void;
  onApplePayLoginRequired?: () => void;
  /** Cart: processCart+Apple Pay. Single project: payOneProject+Apple Pay (same as card). */
  cartMode?: boolean;
  /** Explains a zero total when it is not the donor's missing amount. */
  emptyTotalReason?: string | null;
  projectParams?: UnifiedDonationProjectParams | null;
  /** Apple Pay donor phone (resolved from account, form, or phone step). */
  applePayDonorMobile?: string;
  applePayDonorName?: string;
  bankTransferEnabled: boolean;
  cardEnabled: boolean;
  /** Recurring deduction: only payment paths that can save a token qualify. */
  requiresTokenizedPayment?: boolean;
  banksWithAccounts: BankWithAccounts[];
  isLoadingBanks: boolean;
  selectedBankId: number | null;
  selectedAccountId: number | null;
  donorBankText: string;
  donorBankAccountNum: string;
  receiptImageId: number | null;
  bankTransferError: string | null;
  isUploadingReceipt: boolean;
  isSubmittingBankTransfer: boolean;
  onSelectBank: (value: string) => void;
  onSelectAccount: (value: string) => void;
  onDonorBankTextChange: (value: string) => void;
  onDonorBankAccountNumChange: (value: string) => void;
  onUploadReceipt: (file: File) => void;
  onSubmitBankTransfer: () => void;
  onCardSubmit?: (card: MoyasarTokenizeCardParams) => Promise<void>;
  isCardSubmitting?: boolean;
  cardError?: string | null;
  cardFieldErrors?: MoyasarCardFieldErrors | null;
  showPhoneWithCard?: boolean;
  /** Guest single-project: show the same mobile field on the bank transfer tab. */
  showPhoneWithBank?: boolean;
  form?: UseFormReturn<{ mobile: Value }>;
  showCardFormWithoutPreparation?: boolean;
}

function DonationPaymentStepComponent({
  total,
  paymentStatus,
  paymentError,
  paymentMethodTab,
  setPaymentMethodTab,
  onRetry,
  onBack,
  locale,
  t,
  onApplePaySuccess,
  onApplePayModalError,
  onApplePayLoginRequired,
  cartMode = false,
  emptyTotalReason = null,
  projectParams = null,
  applePayDonorMobile,
  applePayDonorName,
  bankTransferEnabled,
  cardEnabled,
  requiresTokenizedPayment = false,
  banksWithAccounts,
  isLoadingBanks,
  selectedBankId,
  selectedAccountId,
  donorBankText,
  donorBankAccountNum,
  receiptImageId,
  bankTransferError,
  isUploadingReceipt,
  isSubmittingBankTransfer,
  onSelectBank,
  onSelectAccount,
  onDonorBankTextChange,
  onDonorBankAccountNumChange,
  onUploadReceipt,
  onSubmitBankTransfer,
  onCardSubmit,
  isCardSubmitting = false,
  cardError = null,
  cardFieldErrors = null,
  showPhoneWithCard = false,
  showPhoneWithBank = false,
  form,
  showCardFormWithoutPreparation = false,
}: DonationPaymentStepProps) {
  const selectedBank =
    selectedBankId == null
      ? null
      : (banksWithAccounts.find((b) => b.bank.id === selectedBankId) ?? null);
  const selectedAccount =
    selectedBank?.accounts.find((a) => a.id === selectedAccountId) ?? null;

  const showTabs =
    total > 0 &&
    (paymentMethodTab === "bankTransfer" ||
      paymentStatus === "processing" ||
      paymentStatus === "initiating" ||
      paymentStatus === "ready" ||
      showCardFormWithoutPreparation);

  return (
    <div className="space-y- ">
      {total <= 0 && (
        <>
          <Alert variant="destructive">
            <AlertDescription>
              {emptyTotalReason ?? t("checkout.amountRequired")}
            </AlertDescription>
          </Alert>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="w-full"
          >
            {t("common.back")}
          </Button>
        </>
      )}
      {total > 0 && paymentStatus === "error" && (
        <div className="space-y-4">
          <Alert variant="destructive">
            <XCircle className="h-5 w-5" />
            <AlertDescription>{paymentError}</AlertDescription>
          </Alert>
          <Button onClick={onRetry} variant="outline" className="w-full">
            {t("common.tryAgain")}
          </Button>
        </div>
      )}

      {/* A standalone deduction is charged through the cart checkout endpoint so
          its schedule can be created; the single-project Apple Pay flow rejects
          recurring donations, so it is not offered here. */}
      {total > 0 && !projectParams?.isRecurring && (
        <>
          <ApplePayCheckout
            mode={cartMode ? "cart" : "singleProject"}
            requiresTokenizedPayment={requiresTokenizedPayment}
            projectParams={
              cartMode ? undefined : (projectParams ?? undefined)
            }
            total={total}
            singleProjectDonorMobile={applePayDonorMobile}
            singleProjectDonorName={applePayDonorName}
            cartDonorMobile={applePayDonorMobile}
            cartDonorName={applePayDonorName}
            onSuccess={(operationCode) =>
              onApplePaySuccess?.(operationCode)
            }
            onLoginRequired={onApplePayLoginRequired}
          />
          {cardEnabled && bankTransferEnabled && (
            <p className="text-sm text-grey-700 text-center mt-4">
              {t("checkout.orVia")}
            </p>
          )}
        </>
      )}

      {showTabs && (
        <>
          {cardEnabled && bankTransferEnabled && (
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t("checkout.donationMethods")}
            </h3>
          )}
          <Tabs
            value={paymentMethodTab}
            onValueChange={(v) =>
              setPaymentMethodTab(v as "card" | "bankTransfer")
            }
            dir={locale === "ar" ? "rtl" : "ltr"}
            className="w-full"
          >
            {cardEnabled && bankTransferEnabled && (
              <TabsList className="h-auto w-full gap-2 p-0 bg-transparent border-0 shadow-none rounded-lg grid grid-cols-2">
                <TabsTrigger
                  value="card"
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors bg-white",
                    paymentMethodTab === "card"
                      ? "border-primary text-foreground font-bold shadow-none data-[state=active]:border-primary"
                      : "border-border text-foreground data-[state=inactive]:border-border",
                  )}
                >
                  <CreditCardAcceptIcon className="h-5 w-5 text-primary" />
                  <span>{t("checkout.paymentTabCard")}</span>
                </TabsTrigger>

                <TabsTrigger
                  value="bankTransfer"
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors bg-white",
                    paymentMethodTab === "bankTransfer"
                      ? "border-primary text-foreground font-bold shadow-none data-[state=active]:border-primary"
                      : "border-border text-foreground data-[state=inactive]:border-border",
                  )}
                >
                  <BankIcon className="h-5 w-5 text-primary" />
                  <span>{t("checkout.paymentTabBankTransfer")}</span>
                </TabsTrigger>
              </TabsList>
            )}

            <TabsContent
              value="card"
              forceMount
              className="mt-4 bg-background p-4 md:p-6 rounded-xl data-[state=inactive]:hidden data-[state=inactive]:pointer-events-none"
            >
              {cardEnabled && onCardSubmit && (
                <CustomCardForm
                  locale={locale}
                  t={t}
                  total={total}
                  onSubmit={onCardSubmit}
                  isSubmitting={isCardSubmitting}
                  error={cardError}
                  fieldErrors={cardFieldErrors}
                  showPhoneWithCard={showPhoneWithCard}
                  form={form}
                />
              )}
            </TabsContent>

            <TabsContent
              value="bankTransfer"
              forceMount
              className="mt-4 data-[state=inactive]:hidden data-[state=inactive]:pointer-events-none"
            >
              <div className="rounded-xl bg-background dark:bg-slate-800/40 p-6 space-y-4">
                <h3 className="text-lg font-bold">
                  {t("checkout.bankTransfer.title")}
                </h3>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.transferToBank")}
                  </Label>
                  <Select
                    value={selectedBankId ? String(selectedBankId) : undefined}
                    onValueChange={onSelectBank}
                    disabled={isLoadingBanks || isSubmittingBankTransfer}
                    dir={locale === "ar" ? "rtl" : "ltr"}
                  >
                    <SelectTrigger className="w-full h-10 rounded-lg bg-white dark:bg-slate-900 border border-input">
                      <SelectValue
                        placeholder={t("checkout.bankTransfer.selectBank")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {banksWithAccounts.map((bank) => (
                        <SelectItem
                          key={bank.bank.id}
                          value={String(bank.bank.id)}
                        >
                          {bank.bank.nameAr || bank.bank.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.transferToAccount")}
                  </Label>
                  <Select
                    value={
                      selectedAccountId ? String(selectedAccountId) : undefined
                    }
                    onValueChange={onSelectAccount}
                    disabled={!selectedBank || isSubmittingBankTransfer}
                    dir={locale === "ar" ? "rtl" : "ltr"}
                  >
                    <SelectTrigger className="w-full h-10 rounded-lg bg-white dark:bg-slate-900 border border-input">
                      <SelectValue
                        placeholder={t("checkout.bankTransfer.selectBank")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(selectedBank?.accounts || []).map((account) => (
                        <SelectItem key={account.id} value={String(account.id)}>
                          {account.accountName || account.iban}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.iban")}
                  </Label>
                  <div className="relative flex items-center">
                    <Input
                      placeholder="SA00 0000 0000 0000 0000 0000"
                      value={selectedAccount?.iban || ""}
                      className="w-full h-10 rounded-lg bg-white dark:bg-slate-900 border border-input pl-10 rtl:pl-3 rtl:pr-10"
                      readOnly
                    />
                    <button
                      type="button"
                      className="absolute left-2 rtl:left-auto rtl:right-2 inset-y-0 my-auto flex items-center text-primary hover:text-primary/80 p-1 rounded"
                      aria-label={t("common.copy")}
                      onClick={() => {
                        if (selectedAccount?.iban) {
                          navigator.clipboard
                            .writeText(selectedAccount.iban)
                            .catch(() => {});
                        }
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.donorBank")}
                  </Label>
                  <Input
                    value={donorBankText}
                    onChange={(event) =>
                      onDonorBankTextChange(event.target.value)
                    }
                    placeholder={t(
                      "checkout.bankTransfer.donorBankPlaceholder",
                    )}
                    disabled={isSubmittingBankTransfer}
                    className="bg-white dark:bg-slate-900 border-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.donorAccountNumber")}
                  </Label>
                  <Input
                    value={donorBankAccountNum}
                    onChange={(event) =>
                      onDonorBankAccountNumChange(event.target.value)
                    }
                    placeholder={t(
                      "checkout.bankTransfer.donorAccountPlaceholder",
                    )}
                    disabled={isSubmittingBankTransfer}
                    className="bg-white dark:bg-slate-900 border-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("checkout.bankTransfer.uploadTransferImage")}*
                  </Label>
                  <label className="flex items-center gap-2 w-full h-12 rounded-lg bg-white dark:bg-slate-900 border border-input border-dashed cursor-pointer px-4 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors">
                    {isUploadingReceipt ? (
                      <Loader2 className="h-5 w-5 shrink-0 text-primary animate-spin" />
                    ) : (
                      <Upload className="h-5 w-5 shrink-0 text-primary" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {receiptImageId
                        ? t("checkout.bankTransfer.receiptUploaded")
                        : t("checkout.bankTransfer.uploadTransferImage")}
                    </span>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="sr-only"
                      disabled={isUploadingReceipt || isSubmittingBankTransfer}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        if (file) onUploadReceipt(file);
                        event.currentTarget.value = "";
                      }}
                    />
                  </label>
                </div>
                {showPhoneWithBank && form && (
                  <Form {...form}>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Label className="text-sm font-semibold text-foreground">
                          {t("common.mobile")} *
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("checkout.cardPayment.receiptSentToMobile")}
                        </p>
                      </div>
                      <FormField
                        control={form.control}
                        name="mobile"
                        render={({ field }) => (
                          <FormItem className="space-y-2">
                            <FormControl>
                              <PhoneInput
                                placeholder="0000 0000 00"
                                value={field.value}
                                onChange={field.onChange}
                                disabled={isSubmittingBankTransfer}
                                defaultCountry="SA"
                                className={cn(
                                  "bg-white dark:bg-slate-900 border border-input",
                                  "focus-visible:ring-2 focus-visible:ring-primary/20",
                                )}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </Form>
                )}
                {bankTransferError ? (
                  <Alert variant="destructive">
                    <AlertDescription>{bankTransferError}</AlertDescription>
                  </Alert>
                ) : null}
                <Button
                  className="w-full h-13 rounded-lg bg-success hover:bg-success/90 text-base font-bold"
                  onClick={onSubmitBankTransfer}
                  disabled={isSubmittingBankTransfer || isLoadingBanks}
                >
                  {isSubmittingBankTransfer ? (
                    <>
                      <Loader2 className="h-4 w-4 me-2 animate-spin" />
                      {t("common.processing")}
                    </>
                  ) : (
                    t("checkout.bankTransfer.completePayment")
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

export const DonationPaymentStep = memo(DonationPaymentStepComponent);
